/**
 * Flightline — Fleet acquisition pricing
 * --------------------------------------------------------------------------
 * Location : /src/domain/fleet/fleetMath.ts
 * Runs in  : Simulation Web Worker (pure functions, no UI or DOM concerns).
 *
 * Prices for every acquisition route. All inputs come from the aircraft type
 * (`AircraftType`), the airframe age and the prevailing market.
 *
 *   lessor rate multiplier = interest factor × age factor × demand factor
 *     interest factor = 1 + (rate − 4 %) × 0.06, clamped 0.70–1.60
 *     age factor      = 1 − 0.035 × ageYears, floored at 0.50
 *     demand factor   = demandIndex, clamped 0.60–1.60
 *
 *   dry lease rent    = monthlyLeaseUSD × lessor rate multiplier
 *   wet lease (ACMI)  = dry rent × 2.4 (lessor carries crew, maintenance, insurance)
 *   appraised value   = purchaseCostUSD × max(0.12, 0.955^age) × price multiplier
 *   price multiplier  = 1 + 0.15 × (demandIndex − 1), clamped 0.85–1.30
 *   finance instalment = annuity on (financed principal − discounted balloon)
 *
 * Interest rates and demand are inputs. Nothing here reads a clock or RNG.
 */

import { clamp } from '../ai/aiMath';
import { simMinutesToUtcMs } from '../demand/simTime';
import type { AircraftType, SimMinutes, UsdAmount } from '../models';
import type { LeaseSubType, MarketEnvironment } from './types';

/* ========================================================================== *
 * 1. CONSTANTS
 * ========================================================================== */

export const MINUTES_PER_DAY = 1440;
/** Lease and loan periods are 30 days, matching the monthly catalogue cycle. */
export const DAYS_PER_MONTH = 30;
export const MINUTES_PER_MONTH = MINUTES_PER_DAY * DAYS_PER_MONTH;
export const MONTHS_PER_YEAR = 12;
export const DAYS_PER_YEAR = 365;
export const MINUTES_PER_YEAR = MINUTES_PER_DAY * DAYS_PER_YEAR;

export const FACTORY_LEAD_MONTHS_MIN = 6;
export const FACTORY_LEAD_MONTHS_MAX = 24;
export const FACTORY_DEPOSIT_RATE = 0.1;
/** Price escalation over the lead time, per year of lead time. */
export const FACTORY_ESCALATION_PER_YEAR = 0.025;

export const BASE_INTEREST_RATE_PCT = 4;
const RATE_SENSITIVITY_PER_PCT = 0.06;
const RATE_FACTOR_MIN = 0.7;
const RATE_FACTOR_MAX = 1.6;
const AGE_DISCOUNT_PER_YEAR = 0.035;
const AGE_FACTOR_FLOOR = 0.5;
const DEMAND_FACTOR_MIN = 0.6;
const DEMAND_FACTOR_MAX = 1.6;
const PRICE_DEMAND_SENSITIVITY = 0.15;
const PRICE_MULTIPLIER_MIN = 0.85;
const PRICE_MULTIPLIER_MAX = 1.3;

export const ANNUAL_DEPRECIATION_RATE = 0.045;
const RESIDUAL_VALUE_FLOOR = 0.12;

export const WET_LEASE_PREMIUM_MULTIPLE = 2.4;
export const DRY_LEASE_DEPOSIT_MONTHS = 2;
export const FINANCE_DOWN_PAYMENT_RATE = 0.1;
export const FINANCE_BALLOON_RATE = 0.1;
export const FINANCE_SPREAD_PCT = 2.5;
export const FINANCE_PREPAYMENT_FEE_RATE = 0.02;
export const DRY_EARLY_TERMINATION_RENT_SHARE = 0.5;
export const WET_EARLY_TERMINATION_MONTHS = 1;
export const SALE_LEASEBACK_VALUE_FACTOR = 0.9;

/** Lessor requires at least this many months of D-Check validity on return. */
export const RETURN_MIN_D_CHECK_MONTHS = 6;

/** Flight hours and cycles per year of age, used when a lease brings in an airframe with history. */
export const SYNTHETIC_HOURS_PER_YEAR = 2800;
export const SYNTHETIC_HOURS_PER_CYCLE = 2.2;

const FUEL_BURN_PENALTY_PER_YEAR = 0.006;
const FUEL_BURN_PENALTY_CAP = 0.1;

export const DRY_LEASE_TERM_MONTHS = { min: 60, max: 144 } as const;
export const WET_LEASE_TERM_MONTHS = { min: 1, max: 12 } as const;
export const FINANCE_LEASE_TERM_MONTHS = { min: 60, max: 180 } as const;
export const LEASE_AGE_YEARS = { min: 0, max: 20 } as const;

/* ========================================================================== *
 * 2. CALENDAR AND TIME
 * ========================================================================== */

/** Calendar year (UTC) of a simulation instant. */
export function calendarYearOf(now: SimMinutes): number {
  return new Date(simMinutesToUtcMs(now)).getUTCFullYear();
}

/** Whole years between the manufacture year and `now`, never negative. */
export function airframeAgeYears(manufactureYear: number, now: SimMinutes): number {
  return Math.max(0, calendarYearOf(now) - manufactureYear);
}

export function yearsToMinutes(years: number): SimMinutes {
  return years * MINUTES_PER_YEAR;
}

/* ========================================================================== *
 * 3. MARKET MULTIPLIERS
 * ========================================================================== */

/** Scales list prices with lessor demand; neutral (1.0) at demand index 1. */
export function marketPriceMultiplier(market: MarketEnvironment): number {
  return clamp(
    1 + PRICE_DEMAND_SENSITIVITY * (market.demandIndex - 1),
    PRICE_MULTIPLIER_MIN,
    PRICE_MULTIPLIER_MAX
  );
}

export function interestRateFactor(annualInterestRatePct: number): number {
  return clamp(
    1 + (annualInterestRatePct - BASE_INTEREST_RATE_PCT) * RATE_SENSITIVITY_PER_PCT,
    RATE_FACTOR_MIN,
    RATE_FACTOR_MAX
  );
}

export function ageRateFactor(ageYears: number): number {
  return Math.max(AGE_FACTOR_FLOOR, 1 - AGE_DISCOUNT_PER_YEAR * ageYears);
}

export function demandRateFactor(demandIndex: number): number {
  return clamp(demandIndex, DEMAND_FACTOR_MIN, DEMAND_FACTOR_MAX);
}

/** Combined lessor pricing factor for lease rates. */
export function lessorRateMultiplier(market: MarketEnvironment, ageYears: number): number {
  return (
    interestRateFactor(market.annualInterestRatePct) *
    ageRateFactor(ageYears) *
    demandRateFactor(market.demandIndex)
  );
}

/* ========================================================================== *
 * 4. VALUES AND PRICES
 * ========================================================================== */

/** Share of new price a used airframe of this age is worth. Floored, never zero. */
export function residualValueFraction(ageYears: number): number {
  return Math.max(RESIDUAL_VALUE_FLOOR, (1 - ANNUAL_DEPRECIATION_RATE) ** ageYears);
}

/** Market value of an airframe of this type and age at the current prices. */
export function appraisedValueUSD(
  type: AircraftType,
  ageYears: number,
  market: MarketEnvironment
): UsdAmount {
  return type.purchaseCostUSD * residualValueFraction(ageYears) * marketPriceMultiplier(market);
}

export function factoryListPriceUSD(type: AircraftType, market: MarketEnvironment): UsdAmount {
  return type.purchaseCostUSD * marketPriceMultiplier(market);
}

/** List price plus escalation for the time until delivery. Locked at order. */
export function factoryLockedPriceUSD(
  type: AircraftType,
  leadMonths: number,
  market: MarketEnvironment
): UsdAmount {
  const escalation = 1 + FACTORY_ESCALATION_PER_YEAR * (leadMonths / MONTHS_PER_YEAR);
  return factoryListPriceUSD(type, market) * escalation;
}

export function factoryDepositUSD(lockedPriceUSD: UsdAmount): UsdAmount {
  return lockedPriceUSD * FACTORY_DEPOSIT_RATE;
}

/**
 * Penalty for cancelling a factory order, as a share of the locked price.
 * Closer to delivery the penalty is higher. The deposit is credited against it.
 */
export function factoryCancellationRate(monthsToDelivery: number): number {
  if (monthsToDelivery > 12) return 0.1;
  if (monthsToDelivery > 6) return 0.15;
  return 0.25;
}

/** Fuel burn relative to a new airframe. Each year of age adds 0.6 %, capped at +10 %. */
export function fuelBurnMultiplierForAge(ageYears: number): number {
  return 1 + Math.min(FUEL_BURN_PENALTY_CAP, FUEL_BURN_PENALTY_PER_YEAR * ageYears);
}

/* ========================================================================== *
 * 5. LEASE PRICING
 * ========================================================================== */

/** Monthly dry-lease rent for an airframe of this type and age. */
export function dryMonthlyRentUSD(
  type: AircraftType,
  ageYears: number,
  market: MarketEnvironment
): UsdAmount {
  return type.monthlyLeaseUSD * lessorRateMultiplier(market, ageYears);
}

/** Monthly ACMI fee; the premium over dry rent reflects short-term, fully supported capacity. */
export function wetMonthlyFeeUSD(
  type: AircraftType,
  ageYears: number,
  market: MarketEnvironment
): UsdAmount {
  return dryMonthlyRentUSD(type, ageYears, market) * WET_LEASE_PREMIUM_MULTIPLE;
}

/** Annual rate charged on a finance lease: the benchmark rate plus a fixed spread. */
export function financeAnnualRatePct(market: MarketEnvironment): number {
  return market.annualInterestRatePct + FINANCE_SPREAD_PCT;
}

/**
 * Level monthly instalment that amortises `principal` to `balloon` over `termMonths`,
 * paid in arrears at `annualRatePct`. Zero interest reduces to straight-line.
 */
export function monthlyInstalmentUSD(
  principal: UsdAmount,
  balloon: UsdAmount,
  annualRatePct: number,
  termMonths: number
): UsdAmount {
  const monthlyRate = annualRatePct / 100 / MONTHS_PER_YEAR;
  if (monthlyRate === 0) {
    return (principal - balloon) / termMonths;
  }
  const discount = (1 + monthlyRate) ** -termMonths;
  return ((principal - balloon * discount) * monthlyRate) / (1 - discount);
}

/** Lessor covers crew, maintenance and insurance only on wet leases. */
export function lessorCoversCrewAndMaintenance(subType: LeaseSubType): boolean {
  return subType === 'WET';
}
